import "./datadog.client";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  type FieldFunctionOptions,
  type FieldMergeFunction,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { Protect, useAuth } from "@clerk/remix";
import {
  Link,
  NavLink,
  Outlet,
  RemixBrowser,
  useMatches,
  useNavigate,
  useSearchParams,
} from "@remix-run/react";
import { RouterContext, SpinnerContext } from "@repo/ui";
import riveWasmUrl from "@rive-app/canvas/rive.wasm";
import { RuntimeLoader } from "@rive-app/react-canvas";
import countries from "i18n-iso-countries";
import countriesEn from "i18n-iso-countries/langs/en.json";
import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import spinnerUrl from "./assets/animations/primary-spinner.riv";
import possibleTypes from "./gql/client/possibleTypes";
import { customScalarsLink } from "./gql/customScalars";

RuntimeLoader.setWasmUrl(riveWasmUrl);

countries.registerLocale(countriesEn);

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <ApolloProvider
        client={
          new ApolloClient({
            link: ApolloLink.from([
              customScalarsLink,
              (operation, forward) => {
                operation.setContext(
                  ({ headers }: { headers: Record<string, string> }) => ({
                    headers: {
                      "X-Page-Origin": window.location.pathname,
                      ...headers,
                    },
                  }),
                );
                return forward(operation);
              },
              new HttpLink({
                uri: "/graphql",
                credentials: "same-origin",
              }),
            ]),
            cache: new InMemoryCache({
              possibleTypes: possibleTypes.possibleTypes,
              typePolicies: {
                CurrencyAmount: {
                  merge: true,
                },
                Query: {
                  fields: {
                    externalBankAccountDataInstitutions: {
                      keyArgs: ["filter", "distinct"],
                      merge: paginatedMergeFunction("institutions"),
                    },
                    deposits: {
                      keyArgs: ["order"],
                      merge: paginatedMergeFunction("deposits"),
                    },
                    payouts: {
                      keyArgs: ["filter", "order"],
                      merge: paginatedMergeFunction("payouts"),
                    },
                    transactions: {
                      keyArgs: ["filter", "order"],
                      merge: paginatedMergeFunction("transactions"),
                    },
                    transfers: {
                      keyArgs: ["filter", "order"],
                      merge: paginatedMergeFunction("transfers"),
                    },
                    beneficiaryAccounts: {
                      keyArgs: ["filter"],
                      merge: paginatedMergeFunction("beneficiaryAccounts"),
                    },
                    events: {
                      keyArgs: ["filter"],
                      merge: paginatedMergeFunction("events"),
                    },
                    macquarieTransactionImportFiles: {
                      keyArgs: ["order"],
                      merge: paginatedMergeFunction(
                        "macquarieTransactionImportFiles",
                      ),
                    },
                  },
                },
              },
            }),
            defaultOptions: {
              watchQuery: {
                fetchPolicy: "cache-and-network",
                nextFetchPolicy: "cache-first",
                errorPolicy: "all",
              },
            },
          })
        }
      >
        <RouterContext.Provider
          value={{
            Link,
            NavLink,
            Outlet,
            Protect,
            useSearchParams,
            useNavigate,
            useMatches,
            useAuth,
            getIsAdminRoute: () => {
              return !!useMatches().find(
                (match) => match.id === "routes/admin",
              );
            },
          }}
        >
          <SpinnerContext.Provider value={{ spinnerUrl }}>
            <RemixBrowser />
          </SpinnerContext.Provider>
        </RouterContext.Provider>
      </ApolloProvider>
    </StrictMode>,
  );
});

function paginatedMergeFunction(
  itemsKey: string,
): FieldMergeFunction<
  any,
  any,
  FieldFunctionOptions<Record<string, any>, Record<string, any>>
> {
  return function merge(existing, incoming) {
    if (!existing || !incoming) {
      return incoming;
    }

    const existingRefs = new Set<string>(
      existing[itemsKey].map((i: { __ref: string }) => i.__ref),
    );
    const newItems = incoming[itemsKey].filter(
      (i: { __ref: string }) => !existingRefs.has(i.__ref),
    );

    return {
      ...existing,
      ...incoming,
      [itemsKey]: [...existing[itemsKey], ...newItems],
    };
  };
}
